import dynamic from 'next/dynamic';
import Link from 'next/link';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

import ScrollReveal from '@components/common/ScrollReveal';
import { Container } from '@components/parts/Container';

import { CrossSelling as TCrossSelling } from '@customTypes/CrossSelling';
import { DynamicData } from '@customTypes/DynamicData';

const Slider = dynamic(() => import('react-touch-drag-slider'), { ssr: false });

type Props = { services: TCrossSelling } & {
  dynamicData?: DynamicData;
};

const SliderWrapper = styled.div`
  div[data-testid='slider'] {
    width: 324px !important;
    @media (min-width: 992px) {
      width: 350px !important;
    }
  }
  overflow-x: visible;
  height: 390px;
  padding-top: 60px;
  padding-bottom: 60px;
  @media (min-width: 992px) {
    padding-top: 0px;
    padding-bottom: 160px;
    height: 630px;
    margin-left: -12px;
    margin-right: -12px;
  }
  .rtds-slider-styles {
    font: inherit !important;
    word-break: inherit !important;
    .slide-outer {
      touch-action: pan-y !important;
    }
  }
  .rtds-single-slide-styles > div:first-child {
    padding: 0 !important;
  }
`;

const Wrapper = styled.div`
  user-select: none;
  width: 304px;
  height: 300px;
  margin-left: 24px;
  @media (min-width: 992px) {
    margin-left: 0;
    width: 330px;
    height: 300px;
  }
  display: flex;
  div.service {
    position: relative;
    width: 100%;
    color: #62656a;
    display: flex;
    flex-direction: column;
    .image {
      height: 190px;
      background-size: cover;
      background-position: center;
      @media (min-width: 992px) {
        height: 220px;
      }
    }
    .name {
      color: black;
      font-weight: 500;
      font-size: 16px;
      @media (min-width: 992px) {
        font-size: 20px;
      }
      margin-top: 1em;
    }
    .description {
      margin-top: 0.3em;
      font-size: 14px;
      @media (min-width: 992px) {
        font-size: 16px;
      }
    }
  }
`;

const SliderButton = styled.button`
  border: none;
  background: no-repeat url('/svg/review-next.svg');
  &:hover {
    background: no-repeat url('/svg/review-next-hover.svg');
  }
  height: 50px;
  width: 50px;
  outline: none;
`;

const Panel = styled.div`
  display: none;
  @media (min-width: 992px) {
    display: flex;
  }
  align-items: center;
  position: absolute;
  top: 30px;
  bottom: 160px;
  right: 0;
  &.left {
    left: -70px;
    button {
      transform: rotate(180deg);
    }
  }
  &.right {
    right: -70px;
  }
  &.hide {
    display: none;
  }
`;

export const CrossSelling: React.FC<Props> = (props) => {
  const { services: $services, dynamicData } = props;
  const services =
    (dynamicData?.crossSellingServices?.data?.length ? dynamicData?.crossSellingServices?.data : $services.data) || [];

  const [currentSlide, _setCurrentSlide] = useState(0);
  const timerRef = useRef<any>(null);

  const setCurrentSlide = useCallback(
    (idx: number) => {
      if (idx >= services?.length) idx = 0;
      _setCurrentSlide(idx);
    },
    [services?.length]
  );

  const next = (multiplier = 1) => {
    const nextIndex = currentSlide + multiplier * 3;
    return setCurrentSlide(Math.min(services.length - 3, Math.max(0, nextIndex)));
  };

  useEffect(() => {
    setCurrentSlide(0);
  }, [setCurrentSlide]);

  return (
    <Container fullOnMobile style={{ backgroundColor: '#F3F4F8' }} innerStyle={{ position: 'relative' }}>
      <ScrollReveal>
        <Panel className={`left ${currentSlide == 0 ? 'hide' : ''}`}>
          <SliderButton onClick={() => next(-1)} />
        </Panel>
        <Panel className={`right ${currentSlide >= services.length - 3 ? 'hide' : ''}`}>
          <SliderButton onClick={() => next(1)} />
        </Panel>
        <SliderWrapper>
          <Slider
            onSlideComplete={(i) => {
              setCurrentSlide(i);
            }}
            onSlideStart={(i) => {
              if (timerRef.current) {
                clearInterval(timerRef.current);
              }
            }}
            activeIndex={currentSlide}
            threshHold={10}
            transition={0.5}
            scaleOnDrag={false}
          >
            {services.map((service, idx) => (
              <Wrapper key={idx}>
                <div className="service">
                  <Link href={service.attributes.href ?? '/services'}>
                    <div
                      className="image"
                      style={{ backgroundImage: `url('${service.attributes.image?.data?.attributes?.url || ''}')` }}
                    ></div>
                    <div className="name">{service.attributes.name}</div>
                    <p className="description">{service.attributes.description}</p>
                  </Link>
                </div>
              </Wrapper>
            ))}
          </Slider>
        </SliderWrapper>
      </ScrollReveal>
    </Container>
  );
};
